import { createCmsClient } from "@9amhealth/cms-api";
import { useEffect, useMemo, useState } from "react";
import envVariables from "src/lib/envVariables";
import type { CmsImageRootObject } from "src/types/sanitySchema";

/**
 * Fetches Sanity image asset documents by `_ref` from the CMS API.
 * Returns `[]` while loading, or when `refs` is empty.
 *
 * Kept in this module (not SanityCubit) because block-content rendering is
 * ephemeral per-article and doesn't benefit from global cubit state.
 */
export function useSanityAssets(refs: string[]): CmsImageRootObject[] {
  const joinedIds = useMemo(() => refs.filter(Boolean).join(","), [refs]);
  const [assets, setAssets] = useState<CmsImageRootObject[]>([]);

  useEffect(() => {
    if (!joinedIds) {
      setAssets([]);
      return;
    }

    let cancelled = false;
    const client = createCmsClient(envVariables.CMS_API_BASE_URL);
    void client.api.content.assets
      .$get({ query: { ids: joinedIds } })
      .then(async (res) => {
        if (!res.ok) {
          return;
        }
        const { assets: result } = (await res.json()) as unknown as {
          assets: CmsImageRootObject[];
        };
        if (!cancelled) {
          setAssets(result);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [joinedIds]);

  return assets;
}
